/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { Component } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import _ from 'lodash';
import { labels } from '@umati/dashboard-common';
import { anyTodo } from '@umati/dashboard-types';
import '../../styles/filterModal.scss';

interface IBaseDescription<T> {
  cbKey: string;
  value: T;
  default: T;
}

interface ICheckboxDescription extends IBaseDescription<boolean> {
  label: string;
  toggleButton: boolean;
}

interface ISearchBarDescription extends IBaseDescription<string> {
  label: string;
}

interface IDropdownDescriptionBasic extends IBaseDescription<string> {
  label: string;
  choices: string[];
}

interface IDropdownDescriptionAdvanced<T> extends IBaseDescription<T> {
  label: string;
  choices: string[];
  valueToChoice: (value: T) => string;
  choiceToValue: (choice: string) => T;
}

type IDropdownDescription = IDropdownDescriptionBasic | IDropdownDescriptionAdvanced<any>;

interface IToggleChoiceDescription {
  value: string;
  label: string;
  icon: string;
}

interface IToggleDescription extends IBaseDescription<string> {
  choices: IToggleChoiceDescription[];
}

export interface IFilterDescription {
  checkboxes?: ICheckboxDescription[];
  searchbars?: ISearchBarDescription[];
  dropdowns?: IDropdownDescription[];
  toggles?: IToggleDescription[];
}

export interface IFilterModalChild {
  type: string;
  idx: number;
  cbKey: string;
  isVisible: boolean;
}

interface IFilterModalProps {
  filter: IFilterDescription;
  child: IFilterModalChild;
  callback: (filterData: any) => void;
  children?: any;
}

interface IFilterModalState {
  filterCallbackData: anyTodo;
  showFilterWindow: boolean;
  filter: IFilterDescription;
}

class FilterModal extends Component<IFilterModalProps, IFilterModalState> {
  constructor (props) {
    super(props);
    const cbKeys = {};
    Object.keys(this.props.filter).forEach((key) => {
      this.props.filter[key].forEach((el: IBaseDescription<any>) => { cbKeys[el.cbKey] = el.value; });
    });
    this.state = { showFilterWindow: false, filter: _.cloneDeep(this.props.filter), filterCallbackData: cbKeys };
    this.props.callback(this.state.filterCallbackData);
  }

  getFilterCounter = () => {
    let filterCounter = 0;
    const filterState = this.props.filter;
    Object.keys(this.props.filter).forEach((key) => {
      this.props.filter[key].forEach((el, idx) => {
        if (filterState[key] && filterState[key][idx].value !== el.default) {
          filterCounter += 1;
        }
      });
    });
    return filterCounter;
  };

  resetFilter = () => {
    const cbKeys = {};
    const filterState = this.props.filter;
    Object.keys(this.props.filter).forEach((key) => {
      this.props.filter[key].forEach((el, idx) => {
        cbKeys[el.cbKey] = el.default;
        filterState[key][idx].value = el.default;
      });
    });
    this.setState({ ...this.state, filter: _.cloneDeep(filterState), filterCallbackData: cbKeys });
    this.props.callback(cbKeys);
  };

  handleCloseFilterModal = (/* data */) => {
    this.setState({ ...this.state, showFilterWindow: false });
  };

  handleOpenFilterModal = (/* data */) => {
    this.setState({ ...this.state, showFilterWindow: true });
  };

  handleCheckbox = (idx, event) => {
    const state = this.state;
    let targetId: string = '';
    let newValue: boolean | null = null;
    if (event.type === 'click') { // is the button
      targetId = event.target.id.substring(1);
      newValue = !state.filterCallbackData[targetId];
    } else { // is the checkbox
      targetId = event.target.id;
      newValue = event.target.checked;
    }
    if (newValue !== null) {
      state.filterCallbackData[targetId] = newValue;
      if (state.filter.checkboxes?.[idx]) { state.filter.checkboxes[idx].value = newValue; }
      this.setState(state);
      this.props.callback(state.filterCallbackData);
    }
  };

  handleSearch = (idx, event) => {
    const state = this.state;
    state.filterCallbackData[event.target.id] = event.target.value;
    if (state.filter.searchbars?.[idx]) { state.filter.searchbars[idx].value = event.target.value; }
    this.setState(state);
    this.props.callback(state.filterCallbackData);
  };

  handleDropdown = (idx, event) => {
    const state = this.state;
    const filter = state.filter.dropdowns?.[idx];
    let value = event.target.value;
    if (filter) {
      if ('choiceToValue' in filter) {
        value = filter.choiceToValue(value);
      }
      filter.value = value;
    }
    state.filterCallbackData[event.target.id] = value;
    this.setState(state);
    this.props.callback(state.filterCallbackData);
  };

  handleToggle = (idx, cbKey, value) => {
    const state = this.state;
    state.filterCallbackData[cbKey] = value;
    if (state.filter.toggles?.[idx]) { state.filter.toggles[idx].value = value; }
    this.setState(state);
    this.props.callback(state.filterCallbackData);
  };

  childCallback = (data) => {
    const event = {
      target: { id: this.props.child.cbKey, value: data, checked: data }
    };
    switch (this.props.child.type) {
      case 'dropdowns':
        this.handleDropdown(this.props.child.idx, event);
        break;
      default:
        break;
    }
  };

  render () {
    const Children = React.Children.map(
      this.props.children,
      (child) => { return React.isValidElement(child) && React.cloneElement<any>(child, { filterCallback: this.childCallback }); }
    );

    return <div><div className="row justify-content-between align-items-center mb-3 filterbar">
      <div className="col-sm col-xs-12">
        {this.props?.filter?.searchbars?.map((sb, idx) =>
          <div className="input-group input-group-lg searchbox" key={idx}>
            <input type="search"
              className="form-control"
              key={sb.cbKey}
              id={sb.cbKey}
              placeholder={sb.label}
              onChange={e => this.handleSearch(idx, e)} value={sb.value} />
            {
              !sb.value &&
              <div className="input-group-append">
                <button className="btn-search" type="button">{labels.search}</button>
              </div>
            }
          </div>
        )}
      </div>
      <div className="col-auto">
        <div className="btn-group d-flex d-lg-inline-flex" role="group" aria-label="Basic example">
          <button
            className="btn btn-outline-primary btn-icon-filter"
            onClick={this.handleOpenFilterModal}>
            <span onClick={this.handleOpenFilterModal} className="d-none d-sm-inline">{labels.filter}</span>
                  &nbsp;({this.getFilterCounter()})
                </button>
          {
            this.props.filter?.checkboxes?.map((cbval, idx) => cbval.toggleButton &&
              <button
                type="button"
                className={`btn ${cbval.value ? 'btn-primary' : 'btn-outline-primary'}`}
                key={cbval.cbKey}
                id={`b${cbval.cbKey}`}
                onClick={e => this.handleCheckbox(idx, e)}>
                {cbval.label}
              </button>)
          }
          <Modal show={this.state.showFilterWindow} onHide={() => this.setState({ showFilterWindow: false })}>
            <Modal.Header onClick={this.handleCloseFilterModal}>
              <Modal.Title>{labels.filter}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                {this.props?.filter?.checkboxes?.map((cbval, idx) =>
                  <Form.Group key={cbval.label}>
                    <Form.Check className="form-check" type="checkbox" id={cbval.cbKey} label={cbval.label}
                      checked={cbval.value}
                      onChange={e => this.handleCheckbox(idx, e)} />
                  </Form.Group>)
                }
                {this.props?.filter?.dropdowns?.map((dd, idx) =>
                  <Form.Group key={dd.label}>
                    <Form.Label>{dd.label}</Form.Label>
                    <Form.Control as="select" className="form-select" onChange={e => this.handleDropdown(idx, e)} id={dd.cbKey}
                      value={('valueToChoice' in dd) ? dd.valueToChoice(dd.value) : dd.value}>
                      {
                        dd.choices.map(choice => <option key={choice}>{choice}</option>)
                      }
                    </Form.Control>
                  </Form.Group>)
                }
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="reset-filter" onClick={this.resetFilter}> {labels.resetFilter}</Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
      {this.props?.filter?.toggles && <div className="col-auto">
        {
          this.props?.filter?.toggles?.map((tog, idx) =>
            <div className="btn-group d-flex d-lg-inline-flex" role="group" aria-label="Basic example" key={tog.cbKey}>
              {
                tog.choices.map(option =>
                  <button
                    key={option.value}
                    className={`btn ${option.icon} ${tog.value === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => this.handleToggle(idx, tog.cbKey, option.value)}
                  > &nbsp;
                    <span onClick={() => this.handleToggle(idx, tog.cbKey, option.value)} className="d-none d-sm-inline">{option.label}</span>
                  </button>
                )
              }
            </div>
          )
        }
      </div>
      }
    </div>
      {this.props.child.isVisible && Children }
    </div>;
  }
}

export default FilterModal;
